import type { FC, ReactNode } from 'react'
import type { Bilingual } from '@/types/content'
import type { JumpArc } from '@/components/story/arc-jump-nav'

import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'
import LotusMark from '@/components/generals/lotus-mark'
import ArcJumpNav from '@/components/story/arc-jump-nav'

interface StoryHeroProps {
    arcs: JumpArc[]
}

const lead: Bilingual = {
    en: "In the Jain telling, Rama is Padma, the eighth Baladeva, and his life is told by Gautama Ganadhara in answer to King Shrenika's questions. Follow it here in twelve movements, from the first dynasties and the rise of Ravana to the war of Lanka, Sita's ordeal of fire and Rama's liberation.",
    hi: "जैन परम्परा में राम पद्म नाम से आठवें बलभद्र हैं, और उनका चरित्र गौतम गणधर राजा श्रेणिक के प्रश्नों के उत्तर में सुनाते हैं। यहाँ यह कथा बारह खंडों में पढ़िए, पहले वंशों और रावण के उदय से लेकर लंका के युद्ध, सीता की अग्नि-परीक्षा और राम के निर्वाण तक।"
}

const StoryHero: FC<StoryHeroProps> = ({ arcs }): ReactNode => {
    const { locale, t, tx } = useLocale()
    const latin = locale === 'en'

    return (
        <header className='night-sky relative isolate overflow-hidden text-white'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -right-48 -top-40 size-[42rem] rounded-full border border-gold/10 sm:-right-32' />
                <div className='animate-slow-spin absolute -right-24 -top-16 size-[30rem] rounded-full border border-dashed border-gold/20 sm:-right-8' />
                <LotusMark className='absolute -right-6 top-6 size-[16rem] opacity-[0.1] sm:right-12 sm:top-12 sm:size-[20rem] sm:opacity-[0.15]' />
                <div className='absolute inset-x-0 bottom-0 h-56 bg-linear-to-t from-vermilion/20 to-transparent' />
                <div className='gold-rule absolute inset-x-0 bottom-0' />
            </div>

            <div className='mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24'>
                <div className='animate-drift-up'>
                    <p className={cn('flex items-center gap-3 font-medium text-gold-soft', latin ? 'text-xs uppercase tracking-[0.24em]' : 'text-sm')}>
                        <LotusMark className='size-7 shrink-0' />
                        {t.siteTagline}
                    </p>

                    <h1 className={cn('mt-6 max-w-4xl font-heading text-6xl font-semibold text-white sm:text-7xl lg:text-8xl', latin ? 'leading-[1.02]' : 'leading-tight')}>
                        {t.storyTitle}
                    </h1>

                    <div aria-hidden='true' className='mt-7 h-px w-40 bg-linear-to-r from-gold to-transparent' />

                    <p className={cn('mt-7 max-w-3xl text-base text-white/80 sm:text-lg', latin ? 'leading-relaxed' : 'leading-[1.85]')}>
                        {tx(lead)}
                    </p>
                </div>

                <ArcJumpNav arcs={arcs} className='mt-12 sm:mt-14' />
            </div>
        </header>
    )
}

export default StoryHero
